import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GOLD, CRIMSON } from "./AnalysisStation";
import { BONES, RIG_LABELS } from "./pose";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Technical read-outs derived from the Engine's scores (stable while frozen).
export const bioValues = (e) => {
  const bal = e?.balance ?? 5, exe = e?.execution ?? 5, pace = e?.pace ?? 5;
  return {
    joint: Math.round(118 + exe * 4.2),
    stability: Math.min(99, Math.round(52 + bal * 4.6)),
    plant: Math.round(18 + pace * 2.4),
    hip: Math.round(36 + pace * 5.2),
    knee: Math.round(112 + exe * 3.6),
  };
};

export const PANELS = [
  { key: "joint", label: "JOINT_ANGLE", fmt: (v) => `${v.joint}°`, dx: -28, dy: -21 },
  { key: "stability", label: "STABILITY_INDEX", fmt: (v) => `${v.stability}%`, dx: 29, dy: -22 },
  { key: "plant", label: "PLANT_FOOT", fmt: (v) => `${v.plant} cm`, dx: -31, dy: 15 },
  { key: "hip", label: "HIP_DRIVE", fmt: (v) => `${v.hip}%`, dx: 31, dy: 13 },
  { key: "knee", label: "KNEE_FLEXION", fmt: (v) => `${v.knee}°`, dx: 3, dy: 31 },
];

// Lay out all panels as one cluster around the crosshair, sliding the cluster inside the frame (keeps panels from overlapping).
export const layoutPanels = (cx, cy) => {
  const xs = PANELS.map((p) => cx + p.dx), ys = PANELS.map((p) => cy + p.dy);
  const sx = Math.max(0, 13 - Math.min(...xs)) - Math.max(0, Math.max(...xs) - 87);
  const sy = Math.max(0, 9 - Math.min(...ys)) - Math.max(0, Math.max(...ys) - 91);
  return PANELS.map((p) => ({ x: clamp(cx + p.dx + sx, 13, 87), y: clamp(cy + p.dy + sy, 9, 91) }));
};

// Faint ring rippling out from the crosshair once per second — keeps the freeze feeling like a live scan.
const ScanPulse = () => (
  <motion.div className="absolute rounded-full border pointer-events-none" style={{ borderColor: GOLD, width: 36, height: 36, left: -18, top: -18, boxShadow: `0 0 14px ${GOLD}55, inset 0 0 10px rgba(0,229,255,0.28)` }}
    animate={{ scale: [0.35, 3.4], opacity: [0.85, 0] }} transition={{ duration: 1, repeat: Infinity, ease: "easeOut" }} />
);

// Gold targeting reticle pinned to a body joint.
export const LockReticle = ({ label, pulse = false, tracking = false, below = false }) => (
  <div className="absolute" style={{ transform: "translate(-50%,-50%)" }}>
    {pulse && <ScanPulse />}
    <motion.div className="absolute rounded-full border-2" style={{ borderColor: GOLD, width: 56, height: 56, left: -28, top: -28, boxShadow: `0 0 18px ${GOLD}` }}
      initial={{ scale: 1.6, opacity: 0 }} animate={{ scale: 1, opacity: 1, rotate: tracking ? 360 : 0 }}
      transition={{ scale: { duration: 0.3 }, opacity: { duration: 0.3 }, rotate: { duration: 5, repeat: Infinity, ease: "linear" } }}>
      {[[0, "50%"], ["50%", 0], ["100%", "50%"], ["50%", "100%"]].map(([t, l], k) => (
        <div key={k} className="absolute bg-[#F5C518]" style={{ width: 8, height: 2, top: t, left: l, transform: "translate(-50%,-50%)" }} />
      ))}
    </motion.div>
    <div className="absolute bg-[#F5C518]" style={{ width: 1.5, height: 74, left: -0.75, top: -37, opacity: 0.85 }} />
    <div className="absolute bg-[#F5C518]" style={{ height: 1.5, width: 74, top: -0.75, left: -37, opacity: 0.85 }} />
    <div className="absolute rounded-full" style={{ width: 7, height: 7, left: -3.5, top: -3.5, background: GOLD, boxShadow: `0 0 12px ${GOLD}` }} />
    {label && (
      <motion.span animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 0.9, repeat: Infinity }}
        className="absolute font-mono text-[8px] tracking-[0.25em] uppercase text-[#F5C518] whitespace-nowrap bg-black/50 px-1 rounded-sm" style={{ left: 36, top: below ? 30 : -40 }}>{label}</motion.span>
    )}
  </div>
);

const Lead = ({ from, to, i }) => {
  const ex = from.x + (to.x - from.x) * 0.58;
  return (
    <motion.path d={`M${from.x} ${from.y} L${ex} ${to.y} L${to.x} ${to.y}`} fill="none" stroke={GOLD} strokeWidth={1.2}
      vectorEffect="non-scaling-stroke" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 3px ${GOLD})` }}
      initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.95 }}
      transition={{ delay: 0.25 + i * 0.1, duration: 0.45, ease: "easeOut" }} />
  );
};

const Panel = ({ p, pos, v, i }) => (
  <motion.div className="absolute" style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%,-50%)" }}
    initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.58 + i * 0.1, type: "spring", stiffness: 240, damping: 20 }}>
    <div className="relative bg-black/80 border border-[#F5C518]/55 rounded-sm pl-2 pr-1.5 py-0.5 backdrop-blur-sm" style={{ boxShadow: `0 0 14px ${GOLD}44, inset 0 1px 0 rgba(255,255,255,0.08)` }}>
      <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#F5C518]" style={{ boxShadow: `0 0 6px ${GOLD}` }} />
      <div className="font-mono text-[6px] sm:text-[7px] tracking-[0.18em] text-[#F5C518]/70 leading-none">{p.label}</div>
      <div className="font-mono text-[11px] sm:text-[13px] leading-tight text-[#F5C518] font-bold">{p.fmt(v)}</div>
    </div>
  </motion.div>
);

// Glowing gold 13-point biomechanical rig, drawn bone-by-bone over the frozen frame. `pts` are % coords of the frame.
export const Skeleton = ({ pts, focus }) => {
  const bones = BONES.filter(([a, b]) => pts[a] && pts[b]);
  const joints = Object.keys(RIG_LABELS).filter((k) => pts[k]);
  return (
    <>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        {bones.map(([a, b], i) => (
          <motion.line key={`${a}-${b}`} x1={pts[a].x} y1={pts[a].y} x2={pts[b].x} y2={pts[b].y} stroke={GOLD} strokeWidth={2.2} strokeLinecap="round"
            vectorEffect="non-scaling-stroke" style={{ filter: `drop-shadow(0 0 4px ${GOLD})` }}
            initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.95 }} transition={{ delay: 0.12 + i * 0.05, duration: 0.35, ease: "easeOut" }} />
        ))}
      </svg>
      {joints.map((k, i) => (
        <motion.div key={k} className="absolute" style={{ left: `${pts[k].x}%`, top: `${pts[k].y}%`, transform: "translate(-50%,-50%)" }}
          initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 + i * 0.04, type: "spring", stiffness: 300, damping: 18 }}>
          <span className="absolute rounded-full" style={{ width: k === "head" ? 14 : 8, height: k === "head" ? 14 : 8, left: k === "head" ? -7 : -4, top: k === "head" ? -7 : -4, background: "#0a0a0a", border: `1.5px solid ${GOLD}`, boxShadow: `0 0 10px ${GOLD}` }} />
          <motion.span className="absolute rounded-full border" style={{ borderColor: `${GOLD}88`, width: 18, height: 18, left: -9, top: -9 }}
            animate={{ scale: [0.8, 1.6], opacity: [0.7, 0] }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.13, ease: "easeOut" }} />
          {(k === "head" || k.endsWith("hip") || k.endsWith("ank") || k.endsWith("sho")) && (
            <span className="absolute font-mono text-[6px] tracking-[0.15em] text-[#F5C518]/80 whitespace-nowrap bg-black/50 px-1 rounded-sm" style={{ left: k.startsWith("l") ? 10 : "auto", right: k.startsWith("r") ? 10 : "auto", top: -4 }}>{RIG_LABELS[k]}</span>
          )}
        </motion.div>
      ))}
      {focus && (
        <div className="absolute" style={{ left: `${focus.x}%`, top: `${focus.y}%` }}>
          <ScanPulse />
          <motion.span className="absolute rounded-full border-2" style={{ borderColor: CRIMSON, width: 34, height: 34, left: -17, top: -17, boxShadow: `0 0 16px ${CRIMSON}` }}
            initial={{ scale: 2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }} />
        </div>
      )}
    </>
  );
};

// Freeze-Frame HUD: skeletal rig (or pinned reticle) + glowing gold leads to five floating technical panels.
export const FreezeHud = ({ cx, cy, engine, joint, t, active, skeleton, testId }) => {
  const v = bioValues(engine);
  const c = { x: clamp(cx, 4, 96), y: clamp(cy, 4, 96) };
  const spots = layoutPanels(c.x, c.y);
  return (
    <AnimatePresence>
      {active && (
        <motion.div data-testid={testId} className="absolute inset-0 z-30 pointer-events-none"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45, ease: "easeInOut" }}>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            {PANELS.map((p, i) => <Lead key={p.key} from={c} to={spots[i]} i={i} />)}
          </svg>
          {PANELS.map((p, i) => <Panel key={p.key} p={p} pos={spots[i]} v={v} i={i} />)}
          {skeleton ? (
            <div data-testid="skeletal-hud"><Skeleton pts={skeleton} focus={c} /></div>
          ) : (
            <div className="absolute" style={{ left: `${c.x}%`, top: `${c.y}%` }}>
              <LockReticle label={`◎ Lock${joint ? ` · ${joint}` : ""}`} pulse below={c.y < 18} />
            </div>
          )}
          <motion.div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 font-mono text-[7px] sm:text-[8px] tracking-[0.3em] uppercase text-[#F5C518] bg-black/65 px-2.5 py-1 rounded-full border border-[#F5C518]/35 whitespace-nowrap"
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: CRIMSON, boxShadow: `0 0 6px ${CRIMSON}` }} />
            {skeleton ? "Skeletal_Lock" : "Freeze_Frame"}{joint ? ` · ${joint}` : ""}{typeof t === "number" ? ` · T+${t.toFixed(2)}s` : ""}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Sharp digital-glitch flash on the play -> freeze transition.
export const GlitchOverlay = () => (
  <motion.div className="absolute inset-0 z-40 pointer-events-none overflow-hidden"
    initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.4, 1, 0] }} transition={{ duration: 0.38, times: [0, 0.15, 0.4, 0.6, 1] }}>
    <div className="absolute inset-0" style={{ background: "repeating-linear-gradient(0deg, rgba(245,197,24,0.10) 0px, rgba(245,197,24,0.10) 1px, transparent 2px, transparent 4px)", mixBlendMode: "screen" }} />
    <motion.div className="absolute inset-0" style={{ background: "#00e5ff", mixBlendMode: "screen" }}
      initial={{ x: 0, opacity: 0 }} animate={{ x: [-6, 5, 0], opacity: [0.25, 0.15, 0] }} transition={{ duration: 0.3 }} />
    <motion.div className="absolute inset-0" style={{ background: CRIMSON, mixBlendMode: "screen" }}
      initial={{ x: 0, opacity: 0 }} animate={{ x: [6, -5, 0], opacity: [0.25, 0.15, 0] }} transition={{ duration: 0.3 }} />
    {[0, 1, 2].map((i) => (
      <motion.div key={i} className="absolute left-0 right-0 bg-white/50" style={{ height: "5%" }}
        initial={{ top: `${i * 28}%`, opacity: 0 }} animate={{ top: [`${i * 28}%`, `${i * 28 + 66}%`], opacity: [0, 0.85, 0] }} transition={{ duration: 0.35, ease: "linear" }} />
    ))}
  </motion.div>
);
